import { ChevronLeft, ChevronRight } from 'lucide-react';
import { pageWindow } from '@/shared/lib/pageWindow';

const focusRing = 'focus-visible:focus-ring';
const idleClass = 'border-default text-muted hover:border-hover hover:text-brand';

const arrowClass = (disabled: boolean) =>
  `inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors ${focusRing} ${disabled ? 'border-subtle text-disabled' : idleClass}`;

const pageClass = (active: boolean) =>
  `h-7 min-w-7 rounded-lg border px-1.5 text-xs font-semibold tabular-nums transition-colors ${focusRing} ${
    active ? 'border-transparent bg-selected text-on-emphasis shadow-resting' : idleClass
  }`;

type PageNavProps = {
  label: string;
  page: number;
  maxPage: number;
  onPageChange: (page: number) => void;
};

/** Frecce e numeri di pagina di una lista; `label` nomina la lista per i lettori di schermo. */
export default function PageNav({ label, page, maxPage, onPageChange }: PageNavProps) {
  return (
    <nav aria-label={`Pagine: ${label}`} className="flex items-center gap-1">
      <button
        type="button"
        aria-label="Pagina precedente"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        className={arrowClass(page === 1)}
      >
        <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
      </button>
      {pageWindow(page, maxPage).map((pageNum, i) =>
        pageNum === '...' ? (
          <span key={`ellipsis-${i}`} aria-hidden className="px-1 text-xs text-subtle">
            …
          </span>
        ) : (
          <button
            key={pageNum}
            type="button"
            aria-label={`Pagina ${pageNum}`}
            aria-current={page === pageNum ? 'page' : undefined}
            onClick={() => onPageChange(pageNum)}
            className={pageClass(page === pageNum)}
          >
            {pageNum}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Pagina successiva"
        onClick={() => onPageChange(page + 1)}
        disabled={page === maxPage}
        className={arrowClass(page === maxPage)}
      >
        <ChevronRight aria-hidden className="h-3.5 w-3.5" />
      </button>
    </nav>
  );
}
